import React from "react";
import { useTranslation } from "react-i18next";
import { ToastOptions, toast } from "react-toastify";
import { store } from "app/Store";
import { cls } from "app/utils";
import { markOptionalItem, markSetupCta } from "app/actions/academySetup";
import {
  trackRailBackClicked,
  trackRailContinueClicked,
  trackRailDismissed,
  trackRailShown,
} from "app/utils/analytics-helpers/academySetup";
import checkIcon from "app/images/orange_check.svg";
import {
  AcademySetupHintState,
  AcademySetupItem,
  AcademySetupItemKey,
  AcademySetupLocationState,
  allComplete,
  completedCount,
  withCompletion,
} from "../items";
import { settledOptionalStates } from "../transitions";
import styles from "./styles.module.scss";

// The designs place these alerts at the bottom center of the screen rather
// than in react-toastify's default top-right slot (<ToastContainer /> in
// App.tsx sets no position, so the default applies unless overridden here).
const BOTTOM_CENTER: ToastOptions = { position: "bottom-center" };

const AUTO_CLOSE_MS = 8000;

// One live alert at a time: a later completion replaces an earlier one.
let liveRail: { id: string; shownOn: string; replace: () => void } | null =
  null;
let railCount = 0;

/**
 * The location as the rail tracks it — path, query and hash — so switching a
 * tab (/billing?tab=…) counts as leaving the page the alert was shown on.
 */
export const railLocationKey = (
  location: { pathname: string; search: string; hash: string } = window.location,
): string => location.pathname + location.search + location.hash;

/** Closes the live alert once the route leaves the page it was shown on. */
export const leaveSetupRail = (locationKey: string): void => {
  if (liveRail && liveRail.shownOn !== locationKey) {
    toast.dismiss(liveRail.id);
  }
};

/** Closes the live alert wherever it is: the app chrome that owns it is going away. */
export const dismissSetupRail = (): void => {
  if (liveRail) {
    toast.dismiss(liveRail.id);
  }
};

/**
 * A router push. <ToastContainer /> mounts outside <Router />, so the alert
 * can't reach useHistory itself and the call site lends it one.
 */
export type SetupToastNavigate = (
  path: string,
  state?: AcademySetupHintState & AcademySetupLocationState,
) => void;

interface SetupToastProps {
  titleKey: string;
  completedKey: AcademySetupItemKey;
  completed: number;
  total: number;
  /** Next open item after the completed one; null hides the button (Figma 535:26878). */
  continueTo: AcademySetupItem | null;
  navigate: SetupToastNavigate;
  /** Marks the close as an action rather than a dismissal. */
  onAction?: () => void;
  /** Injected by react-toastify when rendered as toast content. */
  closeToast?: () => void;
}

const SetupToast = ({
  titleKey,
  completedKey,
  completed,
  total,
  continueTo,
  navigate,
  onAction,
  closeToast,
}: SetupToastProps) => {
  const { t } = useTranslation();

  const go = (
    path: string,
    state?: AcademySetupHintState & AcademySetupLocationState,
  ) => {
    if (onAction) {
      onAction();
    }
    if (closeToast) {
      closeToast();
    }
    navigate(path, state);
  };

  const onContinue = (item: AcademySetupItem) => {
    trackRailContinueClicked(completedKey, item.key);
    store.dispatch(markSetupCta(item.key, "rail_cta"));
    // Same rule as the checklist CTA: opening an optional item's page counts
    // as complete for progress, whether or not the coach acts there.
    if (item.optional && !item.complete) {
      markOptionalItem(item.key, "visited")(store.dispatch);
    }
    go(`${item.path}${item.search || ""}`, {
      academySetupHint: item.key,
      setupEntryPoint: "rail",
    });
  };

  const onBack = () => {
    trackRailBackClicked(completedKey);
    go("/academy/setup", { setupEntryPoint: "rail" });
  };

  return (
    <div className={styles.container}>
      <div className={styles.summary}>
        <img className={styles.check} src={checkIcon} alt="" />
        <div className={styles.texts}>
          <p className={styles.title}>{t(titleKey)}</p>
          <p className={styles.body}>
            {t(
              "You've completed {{completed}} of the {{total}} steps in your Academy setup.",
              { completed, total },
            )}
          </p>
        </div>
      </div>
      <div className={styles.buttons}>
        {continueTo && (
          <button
            type="button"
            className={cls(styles.button, styles.button_primary)}
            onClick={() => onContinue(continueTo)}
          >
            {t("Continue setup")}
          </button>
        )}
        <button
          type="button"
          className={cls(styles.button, styles.button_secondary)}
          onClick={onBack}
        >
          {t("Back to setup")}
        </button>
      </div>
    </div>
  );
};

/** A `useActions`-bound `fetchAcademySetup`. */
type RefetchSetup = (force?: boolean) => Promise<unknown>;

/**
 * Whether the checklist still has `key` open per the last fetched completion.
 *
 * The alerts below only ask the API "is this complete now?", never "did it
 * just flip" — so a call site that fires on every save would re-celebrate an
 * item the coach finished weeks ago. Read this before the save to tell the
 * two apart. `null` (nothing fetched yet) reads as closed: celebrate on
 * evidence the item was open, not on the absence of evidence.
 */
export const isSetupItemOpen = (key: AcademySetupItemKey): boolean => {
  const { academySetup, user } = store.getState();
  // A slice fetched for another account (the store survives sign-out) is
  // no evidence about this one.
  const completion =
    academySetup.ownerId === (user?.self?.id ?? null) ? academySetup.data : null;
  return completion !== null && !completion[key];
};

const CONFIRM_RETRY_DELAY_MS = 1000;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Refetches until the API reports `itemKey` complete, or `attempts` runs out.
 * Returns the confirming items so the count comes from the same response.
 *
 * One attempt suits an action whose own request wrote the completion. Actions
 * the BE finishes afterwards need several: POST /space_imports queues an
 * import, so the spaces (and the invitations that complete invite_athlete)
 * appear seconds after the create resolves.
 */
const confirmComplete = async (
  itemKey: AcademySetupItemKey,
  refetch: RefetchSetup,
  attempts: number,
): Promise<AcademySetupItem[] | null> => {
  // The account that saved; a slice that has since been fetched for another
  // one (sign-out is an in-app navigation) is no confirmation of its save.
  const userId = store.getState().user?.self?.id ?? null;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) {
      await delay(CONFIRM_RETRY_DELAY_MS);
    }
    // Forced: a badge refetch already in flight would otherwise dedupe this
    // away and leave the read below on pre-save completion.
    await refetch(true);

    const { academySetup, user } = store.getState();
    const { ownerId, data, optionalStates, pendingOptional } = academySetup;
    // The slice must be this account's, and this account must still be the
    // one signed in: after a sign-out the refetch is a no-op and the slice
    // keeps the old owner.
    if (ownerId !== userId || (user?.self?.id ?? null) !== userId) {
      return null;
    }
    // Without the optimistic marks whose PUT is still out: a mark that is
    // then rolled back must not have counted toward the rail's progress or
    // moved its Continue target past the row.
    const items = withCompletion(
      data,
      settledOptionalStates(optionalStates, pendingOptional),
    );
    // `done`, not `complete`: a skipped/visited optional item counts toward
    // progress, but the alert must wait for the real flag (a coach who
    // skipped Stripe and then failed to connect gets no "connected!").
    if (items.find((item) => item.key === itemKey)?.done) {
      return items;
    }
  }
  return null;
};

/**
 * The rail's "Continue setup" target (spec §4: the next incomplete item in
 * phase order): the first open item in checklist order, other than the one
 * just completed. Phase order is the recommended path, so a coach who jumped
 * ahead (say, published the academy page with availability still open) is
 * sent back to the earliest gap rather than on past it (product ask
 * 2026-09-09, replacing the "next after the completed item" reading). An
 * untouched optional item is open; a visited or skipped one is not. Null only
 * when the list is fully complete.
 */
export const nextSetupItem = (
  items: AcademySetupItem[],
  completedKey: AcademySetupItemKey,
): AcademySetupItem | null => {
  return (
    items.find((item) => !item.complete && item.key !== completedKey) || null
  );
};

/**
 * Refetches the checklist and fires the alert only if the API agrees the item
 * is done — the BE owns completion, not the local save that just succeeded.
 * That also makes the X/Y count an API value rather than an optimistic guess.
 *
 * @param mayShowOn Where the rail may still appear once confirmed, for a
 * flow that navigates by design while the item is confirmed (the space
 * import). Default: only the page the save happened on — a confirmation
 * that arrives after the coach has left shows nothing.
 * @returns whether the confirmation has been handled: the alert was shown,
 * or the coach has left the page and nothing should follow them — whether
 * the API confirmed the item or the poll gave up. Callers fall back to their
 * generic confirmation on false, so a save is never left unacknowledged on
 * the page it happened on.
 */
const showSetupToast = async (
  titleKey: string,
  itemKey: AcademySetupItemKey,
  refetch: RefetchSetup,
  navigate: SetupToastNavigate,
  options?: ToastOptions,
  attempts: number = 1,
  mayShowOn?: (pathname: string) => boolean,
): Promise<boolean> => {
  const origin = railLocationKey();
  const items = await confirmComplete(itemKey, refetch, attempts);
  // Handled by showing nothing: the caller's generic toast would otherwise
  // surface on whatever page the coach moved on to. That holds when the poll
  // gave up as much as when it confirmed — the invitation poll runs ~15 s,
  // long enough to be on another page by the time it does.
  const left =
    railLocationKey() !== origin &&
    !(mayShowOn && mayShowOn(window.location.pathname));
  if (!items) {
    return left;
  }
  if (left) {
    return true;
  }
  // No rail once everything is complete; the caller shows its generic
  // confirmation. An earlier rail still up goes too: its Continue target is
  // now complete, and §4 shows nothing at 9/9.
  if (allComplete(items)) {
    if (liveRail) {
      liveRail.replace();
    }
    return false;
  }

  const next = nextSetupItem(items, itemKey);
  const shownOn = railLocationKey();
  // Buttons set this before closing; any other close is a dismissal.
  let acted = false;
  // A replaced alert still runs onClose; that is not a dismissal.
  let replaced = false;
  if (liveRail) {
    liveRail.replace();
  }
  railCount += 1;
  const id = `academy-setup-rail-${railCount}`;
  liveRail = {
    id,
    shownOn,
    replace: () => {
      replaced = true;
      toast.dismiss(id);
    },
  };

  toast(
    <SetupToast
      titleKey={titleKey}
      completedKey={itemKey}
      completed={completedCount(items)}
      total={items.length}
      continueTo={next}
      navigate={navigate}
      onAction={() => {
        acted = true;
      }}
    />,
    {
      toastId: id,
      className: styles.toast,
      bodyClassName: styles.toast_body,
      icon: false,
      closeButton: false,
      hideProgressBar: true,
      closeOnClick: false,
      autoClose: AUTO_CLOSE_MS,
      // react-toastify pauses the timer on hover and on window blur by default,
      // which left the rail parked over the page (and over the Availability
      // "Save changes" button beneath it) for as long as the cursor rested on
      // it. Spec §4 wants an 8 s rail, so let the timer run regardless.
      pauseOnHover: false,
      pauseOnFocusLoss: false,
      onClose: () => {
        if (liveRail && liveRail.id === id) {
          liveRail = null;
        }
        if (!acted && !replaced) {
          trackRailDismissed(
            itemKey,
            railLocationKey() === shownOn ? "explicit" : "navigation",
          );
        }
      },
      ...options,
    },
  );
  trackRailShown(itemKey, next ? next.key : null, window.location.pathname);

  return true;
};

/**
 * Fired when saving the Facility card on /manage_facility (Admin Settings →
 * General) fills in the name and address the facility_profile item checks.
 * Spec §3 expects this item pre-checked by the wizard, but a coach who left
 * the wizard's facility step early arrives with it open, so the checklist's
 * own CTA needs the same rail as every other item. No designed frame — the
 * title follows the other rails' wording (product ask 2026-09-08).
 */
export const showFacilityProfileSavedToast = (
  refetch: RefetchSetup,
  navigate: SetupToastNavigate,
): Promise<boolean> => {
  return showSetupToast(
    "Facility Profile Created!",
    "facility_profile",
    refetch,
    navigate,
    BOTTOM_CENTER,
  );
};

/** The designed "Stripe Connected!" alert (Figma 531:14666). */
export const showConnectStripeToast = (
  refetch: RefetchSetup,
  navigate: SetupToastNavigate,
): Promise<boolean> => {
  return showSetupToast(
    "Stripe Connected!",
    "stripe_connect",
    refetch,
    navigate,
    BOTTOM_CENTER,
  );
};

/** ~15s of grace for the space import to land before giving up. */
const INVITE_CONFIRM_ATTEMPTS = 15;

/**
 * The designed "Your first athlete is invited!" alert (Figma 535:26878).
 * Safe to call as soon as the create resolves: it waits for the API to
 * confirm the item.
 */
export const showInvitationSentToast = (
  refetch: RefetchSetup,
  navigate: SetupToastNavigate,
): Promise<boolean> => {
  return showSetupToast(
    "Your first athlete is invited!",
    "invite_athlete",
    refetch,
    navigate,
    BOTTOM_CENTER,
    // The import runs server-side after the create resolves, so the flag
    // flips a beat later — poll it rather than asking once and giving up.
    INVITE_CONFIRM_ATTEMPTS,
    // The create flow routes to the new space (and back to /spaces) while
    // that poll runs; anywhere else means the coach has moved on.
    (pathname) => /^\/spaces(\/|$)/.test(pathname),
  );
};

/**
 * Fired when the coach's first active booking option lands on
 * /calendar/pricing — the moment the booking_options item flips
 * (Figma 409:15277).
 */
export const showBookingOptionCreatedToast = (
  refetch: RefetchSetup,
  navigate: SetupToastNavigate,
): Promise<boolean> => {
  return showSetupToast(
    "Booking Option Created!",
    "booking_options",
    refetch,
    navigate,
    BOTTOM_CENTER,
  );
};

/**
 * Fired when saving on /calendar/availability turns the coach's first
 * availability day on — the moment the calendar_options item flips
 * (Figma 531:10559).
 */
export const showAvailabilitySetToast = (
  refetch: RefetchSetup,
  navigate: SetupToastNavigate,
): Promise<boolean> => {
  return showSetupToast(
    "Availability setup complete!",
    "calendar_options",
    refetch,
    navigate,
    BOTTOM_CENTER,
  );
};

/** Enough grace for a Stripe round-trip to leave the product enabled. */
const OFFERING_CONFIRM_ATTEMPTS = 3;

/**
 * Fired when the coach's first active package or subscription lands from
 * /payment/create or /subscription/create — the packages_subscriptions item
 * (Figma 531:17780).
 */
export const showOfferingCreatedToast = (
  refetch: RefetchSetup,
  navigate: SetupToastNavigate,
): Promise<boolean> => {
  return showSetupToast(
    "You have successfully created your first offering!",
    "packages_subscriptions",
    refetch,
    navigate,
    BOTTOM_CENTER,
    // Creating the product round-trips to Stripe, so `enabled` can land a
    // beat after the POST resolves — ask more than once before giving up.
    OFFERING_CONFIRM_ATTEMPTS,
  );
};

/**
 * Fired when the Public Coach Landing Page toggle on /account/coach-profile
 * is switched on — the moment the coach_profile item flips (Figma 535:18749).
 */
export const showCoachProfileEnabledToast = (
  refetch: RefetchSetup,
  navigate: SetupToastNavigate,
): Promise<boolean> => {
  return showSetupToast(
    "Your Public Page is Live!",
    "coach_profile",
    refetch,
    navigate,
    BOTTOM_CENTER,
  );
};

/**
 * Fired when the Facility Landing Page toggle on /manage_facility flips the
 * checklist's academy_page flag (Figma 559:27594). Enabling the toggle alone
 * doesn't always satisfy the BE's check, which is what the gate above is for.
 */
export const showPublicPresenceToast = (
  refetch: RefetchSetup,
  navigate: SetupToastNavigate,
): Promise<boolean> => {
  return showSetupToast(
    "Facility Landing Page is Live!",
    "academy_page",
    refetch,
    navigate,
    BOTTOM_CENTER,
  );
};

export default SetupToast;
